import { dataService } from '../../data';

import { DataObject, ObjectOptions, RelatedObject } from '../base';
import { CreateObject } from '../item';
import { PaymentView, PaymentViewProxy } from '../views/payment';

import { PlaceType } from '../types';
import { TicketType } from '../types';
import { SwinvoiceType } from '../types';

import { Place } from './place';

/****************************/
/* SCHEMA DEFINITION        */
/****************************/

const _schema = {
    name: 'PAYMENT',
    fields: [
        { name: 'created', type: 'date', volatile: true },
        { name: 'updated', type: 'date', volatile: true },
        { name: 'place', type: 'objid' },
        { name: 'status', type: 'string', default: 'PA' },
        { name: 'payer', type: 'string' },
        { name: 'ticket', type: 'objid' },
        { name: 'reason', type: 'string' },
        { name: 'amount', type: 'number' },
        { name: 'currency', type: 'string' },
        { name: 'txnid', type: 'string' },
        { name: 'type', type: 'string' },
        { name: 'period', type: 'string' },
        { name: 'concepts', type: 'string' },
        { name: 'taxrate', type: 'number' },
        { name: 'swinvoice', type: 'objid' },
    ],
    relate: [
        { direction: '>', target: 'PLACE', by: 'place', name: 'place', reason: 'place', child: false, class: PlaceType },
        { direction: '>', target: 'TICKET', by: 'ticket', name: 'ticket', reason: 'ticket', child: false, class: TicketType },
        { direction: '>', target: 'SWINVOICE', by: 'swinvoice', name: 'swinvoice', reason: 'swinvoice', child: false, class: SwinvoiceType },
    ],
} as const;

const _PaymentClass = CreateObject(_schema);
if (!_PaymentClass) {
    throw new Error("Failed to create BaseClass for '" + _schema.name + "'");
}

export type LicensePaymentConcept = {
    id: string;
    labelKey: string;
    quantity: number;
    unitPrice: number;
    amount: number;
};

/****************************/
/* CLASS DECLARATION        */
/****************************/

export class Payment extends _PaymentClass {
    constructor(objid: string | null, data: dataService, objoptions: ObjectOptions | null = null) {
        super(_schema, objid, data, objoptions || {});
    }

    Copy(store: Array<DataObject> = []): Payment {
        return this._Copy(store) as Payment;
    }

    override get viewProxy(): PaymentView {
        return PaymentViewProxy(this, this.data);
    }

    /**
     * A PAYMENT row arriving alone (license checkout publishes only the PAYMENT) does not carry
     * the parent PLACE children list, so `place.transactions` would stay stale until a full
     * reload. Register in / unregister from the parent when the `place` link changes.
     */
    protected override DoChildLinked(relation: string | null, related: RelatedObject | null): void {
        if (relation !== 'place' || !(related instanceof Place)) {
            return;
        }

        if (related === this.place) {
            related.AddTransaction(this);
        }
        else {
            related.DelTransaction(this);
        }
    }

    get IsDone(): boolean {
        return this.status === 'DN';
    }

    get IsError(): boolean {
        return this.status === 'ER';
    }

    get IsPending(): boolean {
        return this.status === 'PA';
    }

    /** Renewal period for license payments; null for ticket payments. */
    get Period(): '1M' | '3M' | '1Y' | null {
        const _period = this.period;
        if (_period === '1M' || _period === '3M' || _period === '1Y') {
            return _period;
        }
        return null;
    }

    /** Snapshot of billed license concepts; empty for ticket payments. */
    get Concepts(): LicensePaymentConcept[] {
        const _raw = this.concepts;
        if (!_raw) {
            return [];
        }
        try {
            const _parsed = JSON.parse(_raw);
            return Array.isArray(_parsed) ? _parsed : [];
        } catch {
            return [];
        }
    }

    /** Tax rate (%) snapshot at payment time; null for ticket payments. */
    get TaxRate(): number | null {
        const _rate = this.taxrate;
        return _rate == null ? null : Number(_rate);
    }

    get TaxTotal(): number {
        const _amount = Number(this.amount ?? 0);
        const _rate = this.TaxRate ?? 0;
        return Math.round(_amount * _rate) / 100;
    }

    get TotalWithTax(): number {
        return Number(this.amount ?? 0) + this.TaxTotal;
    }

    /** Fiscal system from related platform SWINVOICE (license payments only). */
    get FiscalSystem(): 'TICKETBAI' | 'VERIFACTU' | null {
        const _system = this.swinvoice?.agency;
        if (_system === 'TICKETBAI' || _system === 'VERIFACTU') {
            return _system;
        }
        return null;
    }

    get FiscalId(): string | null {
        const _id = this.swinvoice?.id;
        return _id ? String(_id) : null;
    }

    get FiscalQr(): string | null {
        const _qr = this.swinvoice?.qr;
        return _qr ? String(_qr) : null;
    }

    get FiscalSeries(): string | null {
        const _series = this.swinvoice?.series;
        return _series ? String(_series) : null;
    }

    get FiscalNumber(): number | null {
        const _number = this.swinvoice?.number;
        return _number == null ? null : Number(_number);
    }

    /** Prefer Gekkotech series/number when linked; else txnid/objid. */
    get FiscalInvoiceRef(): string | null {
        const _series = this.FiscalSeries;
        const _number = this.FiscalNumber;
        if (_series && _number != null) {
            return `${_series}/${_number}`;
        }
        return this.txnid || this.objid || null;
    }
}
